<script lang="ts">
  import { onDestroy, onMount } from "svelte"
  import { get } from "svelte/store"
  import { removeBanner, upsertBanner } from "@kontextwerk/web-sdk/core/runtime/bannerStore"
  import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import { registerEventCallback, unregisterEventCallback } from "../../../_shared/_logic/eventbus"
  import {
    customerImportStatus,
    customerImportStatusOrganizationId,
    refreshCustomerImportStatus,
    resetCustomerImportStatus,
  } from "../../../_shared/_logic/customer-import-status"
  import CustomerImportStatusOverlay from "./_modules/customer-import-status-overlay"
  import { customerImportStatusPresentation } from "./_shared/_logic/status-presentation"

  const BANNER_ID = "kehrblatt-customer-import-active"
  const ACTIVE_POLL_MS = 5000
  const IDLE_POLL_MS = 30000

  let timer: ReturnType<typeof setTimeout> | null = null
  let unsubscribeStatus: (() => void) | null = null
  let unsubscribeOrganization: (() => void) | null = null
  let activeOrganizationId: string | null = null

  const currentOrganizationId = () => get(customerImportStatusOrganizationId)

  const syncBanner = () => {
    const organizationId = currentOrganizationId()
    const state = get(customerImportStatus)
    const active = state.status

    if (!organizationId || state.organizationId !== organizationId) {
      removeBanner(BANNER_ID)

      return
    }

    if (!active?.active || !active.status) {
      removeBanner(BANNER_ID)

      return
    }

    const progress = customerImportStatusPresentation(active.status)
    upsertBanner({
      id: BANNER_ID,
      title: progress.indeterminate ? progress.label : "Kehrbezirksimport läuft",
      body: "Während der Import verarbeitet wird, sind Änderungen am Kehrbezirk gesperrt.",
      severity: "info",
      dismissible: false,
      priority: 80,
      // The existing banner has no indeterminate bar. Its running headline
      // replaces the completed row bar until the server confirms completion.
      progress: progress.indeterminate
        ? null
        : {
            value: progress.processed,
            max: progress.total || 1,
            label: progress.label,
          },
      action: {
        label: "Fortschritt anzeigen",
        onClick: () => {
          openOverlay({
            title: "Kehrbezirksimport",
            content: CustomerImportStatusOverlay,
            isDark: false,
          })
        },
      },
    } as any)
  }

  const scheduleNext = (active: boolean) => {
    if (timer) clearTimeout(timer)

    timer = setTimeout(tick, active ? ACTIVE_POLL_MS : IDLE_POLL_MS)
  }

  const tick = async () => {
    const organizationId = currentOrganizationId()

    if (!organizationId) {
      resetCustomerImportStatus(null)
      removeBanner(BANNER_ID)
      scheduleNext(false)

      return
    }

    const status = await refreshCustomerImportStatus(organizationId)
    syncBanner()
    scheduleNext(Boolean(status?.active))
  }

  onMount(() => {
    unsubscribeStatus = customerImportStatus.subscribe(syncBanner)
    unsubscribeOrganization = customerImportStatusOrganizationId.subscribe((organizationId) => {
      if (activeOrganizationId === organizationId) return

      activeOrganizationId = organizationId
      resetCustomerImportStatus(organizationId)
      removeBanner(BANNER_ID)
      void tick()
    })
    const importHandlerId = "customer-import-status-banner"
    const registryHandlerId = "customer-import-status-registry"
    const refresh = () => {
      void tick()
    }
    registerEventCallback("assist.import_progress", importHandlerId, refresh)
    registerEventCallback("customer-registry.changed", registryHandlerId, refresh)
    void tick()

    return () => {
      unregisterEventCallback("assist.import_progress", importHandlerId)
      unregisterEventCallback("customer-registry.changed", registryHandlerId)
    }
  })

  onDestroy(() => {
    if (timer) clearTimeout(timer)

    unsubscribeStatus?.()
    unsubscribeOrganization?.()
    removeBanner(BANNER_ID)
    resetCustomerImportStatus(currentOrganizationId())
  })
</script>
